/**
 * SRS API client.
 *
 * Wraps every /api/srs route. The shape mirrors the backend FastAPI
 * surface exactly — no client-side translation, no parallel models.
 * Cards are notes; the same id resolves at both /api/srs/cards/{id} and
 * /api/notes/{id}, returned in different projections.
 */

import { apiClient, buildQueryString } from "@/shared/lib/api/client";
import type {
  CardLibraryResponse,
  CardsFromSourceInput,
  DueCardsResponse,
  GradeRequest,
  GradeResponse,
  NextDueCountdown,
  ReviewRequestInput,
  ReviewResponse,
  SchedulePreview,
  SRSCard,
  SRSCardCreateInput,
  SRSCardUpdateInput,
  SRSLens,
  SRSStats,
  StudyNote,
  StudyNoteCreateInput,
  StudyNoteKind,
  StudyNoteListResponse,
  UpcomingCardsResponse,
  ReviewLogItem,
} from "../types";

const BASE = "/api/srs";

// ===================
// Card writes
// ===================

export function createCard(data: SRSCardCreateInput): Promise<SRSCard> {
  return apiClient<SRSCard>(`${BASE}/cards`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function createCardsFromSource(
  data: CardsFromSourceInput,
): Promise<SRSCard[]> {
  return apiClient<SRSCard[]>(`${BASE}/cards/from-source`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function deleteCard(cardId: string): Promise<void> {
  return apiClient<void>(`${BASE}/cards/${cardId}`, { method: "DELETE" });
}

// ===================
// Card reads
// ===================

export interface ListCardsFilter {
  skip?: number;
  limit?: number;
  state?: SRSLens;
  search?: string;
  sort?: "created_at" | "updated_at" | "title";
  sort_dir?: "ASC" | "DESC";
}

export function listCards(
  filter?: ListCardsFilter,
): Promise<CardLibraryResponse> {
  const qs = buildQueryString({
    skip: filter?.skip ?? 0,
    limit: filter?.limit ?? 20,
    state: filter?.state,
    search: filter?.search,
    sort: filter?.sort ?? "created_at",
    sort_dir: filter?.sort_dir ?? "DESC",
  });
  return apiClient<CardLibraryResponse>(`${BASE}/cards${qs}`);
}

export function getCard(cardId: string): Promise<SRSCard> {
  return apiClient<SRSCard>(`${BASE}/cards/${cardId}`);
}

export function getSchedulePreview(cardId: string): Promise<SchedulePreview> {
  return apiClient<SchedulePreview>(`${BASE}/cards/${cardId}/preview`);
}

export function getReviewHistory(
  cardId: string,
  params?: { skip?: number; limit?: number },
): Promise<{ reviews: ReviewLogItem[]; total: number }> {
  const qs = buildQueryString({
    skip: params?.skip ?? 0,
    limit: params?.limit ?? 50,
  });
  return apiClient(`${BASE}/cards/${cardId}/history${qs}`);
}

// ===================
// Due / upcoming / countdown
// ===================

export function listDue(params?: {
  limit?: number;
  includeNew?: boolean;
  includeReview?: boolean;
  includeLearning?: boolean;
}): Promise<DueCardsResponse> {
  const qs = buildQueryString({
    limit: params?.limit,
    include_new: params?.includeNew,
    include_review: params?.includeReview,
    include_learning: params?.includeLearning,
  });
  return apiClient<DueCardsResponse>(`${BASE}/due${qs}`);
}

export function listUpcoming(limit = 20): Promise<UpcomingCardsResponse> {
  return apiClient<UpcomingCardsResponse>(
    `${BASE}/upcoming${buildQueryString({ limit })}`,
  );
}

export function getNextDueCountdown(): Promise<NextDueCountdown> {
  return apiClient<NextDueCountdown>(`${BASE}/next-due-countdown`);
}

// ===================
// Review
// ===================

export function reviewCard(data: ReviewRequestInput): Promise<ReviewResponse> {
  return apiClient<ReviewResponse>(`${BASE}/review`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ===================
// Stats
// ===================

export function getStats(): Promise<SRSStats> {
  return apiClient<SRSStats>(`${BASE}/stats`);
}

// ===================
// Bulk ops
// ===================

export function suspendCards(
  cardIds: string[],
): Promise<{ suspended: number }> {
  return apiClient(`${BASE}/suspend`, {
    method: "POST",
    body: JSON.stringify({ card_ids: cardIds }),
  });
}

export function unsuspendCards(
  cardIds: string[],
): Promise<{ unsuspended: number }> {
  return apiClient(`${BASE}/unsuspend`, {
    method: "POST",
    body: JSON.stringify({ card_ids: cardIds }),
  });
}

export function buryCards(
  cardIds: string[],
  until?: string,
): Promise<{ buried: number }> {
  return apiClient(`${BASE}/bury`, {
    method: "POST",
    body: JSON.stringify({ card_ids: cardIds, until }),
  });
}

export function unburyCards(
  cardIds: string[],
): Promise<{ unburied: number }> {
  return apiClient(`${BASE}/unbury`, {
    method: "POST",
    body: JSON.stringify({ card_ids: cardIds }),
  });
}

// ===================
// Study notes (Learn tab)
// ===================

export function listCardLearn(
  cardId: string,
  kind?: StudyNoteKind | "all",
): Promise<StudyNoteListResponse> {
  const qs = buildQueryString({
    kind: kind && kind !== "all" ? kind : undefined,
  });
  return apiClient<StudyNoteListResponse>(
    `${BASE}/cards/${cardId}/learn${qs}`,
  );
}

export function attachStudyNote(
  cardId: string,
  data: StudyNoteCreateInput,
): Promise<StudyNote> {
  return apiClient<StudyNote>(`${BASE}/cards/${cardId}/learn`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function detachStudyNote(
  cardId: string,
  noteId: string,
): Promise<void> {
  return apiClient<void>(`${BASE}/cards/${cardId}/learn/${noteId}`, {
    method: "DELETE",
  });
}

// ===================
// Card edit + LLM grading (Concept-cockpit)
// ===================

export function updateCard(
  cardId: string,
  patch: SRSCardUpdateInput,
): Promise<SRSCard> {
  return apiClient<SRSCard>(`${BASE}/cards/${cardId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function gradeCardRecall(
  cardId: string,
  data: GradeRequest,
): Promise<GradeResponse> {
  return apiClient<GradeResponse>(`${BASE}/cards/${cardId}/grade`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function archiveCards(
  cardIds: string[],
): Promise<{ archived: number }> {
  return apiClient(`${BASE}/archive`, {
    method: "POST",
    body: JSON.stringify({ card_ids: cardIds }),
  });
}

export function unarchiveCards(
  cardIds: string[],
): Promise<{ unarchived: number }> {
  return apiClient(`${BASE}/unarchive`, {
    method: "POST",
    body: JSON.stringify({ card_ids: cardIds }),
  });
}
